import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Briefcase, FileText, Download } from "lucide-react";
import { BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Booking = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    event_type: "",
    location: "",
    budget: "",
    event_date: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.event_type || !form.location) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request received. We'll be in touch soon.");
      setForm({
        full_name: "",
        email: "",
        event_type: "",
        location: "",
        budget: "",
        event_date: "",
        details: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-4 mb-4">
            <span className="h-px w-10 bg-[color:var(--gold)]" />
            <span className="overline">Booking & Press</span>
          </div>
          <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Let&apos;s create<br />
            <span className="font-display italic gold-text">something epic.</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-md">
            For shows, festivals, brand partnerships, features, or press — drop a note. Real eyes read every message.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 grid place-items-center rounded-full glass shrink-0"><Mail size={14} className="gold-text" /></span>
              <div>
                <p className="overline mb-1">Booking</p>
                <a href={`mailto:${BRAND.email}`} className="text-white/85 hover:text-[color:var(--gold)]">{BRAND.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 grid place-items-center rounded-full glass shrink-0"><Briefcase size={14} className="gold-text" /></span>
              <div>
                <p className="overline mb-1">Management</p>
                <p className="text-white/85">{BRAND.management}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 grid place-items-center rounded-full glass shrink-0"><FileText size={14} className="gold-text" /></span>
              <div>
                <p className="overline mb-1">Press</p>
                <a href={`mailto:${BRAND.press}`} className="text-white/85 hover:text-[color:var(--gold)]">{BRAND.press}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 grid place-items-center rounded-full glass shrink-0"><Download size={14} className="gold-text" /></span>
              <div className="min-w-0">
                <p className="overline mb-1">Press Release</p>
                <a
                  href={BRAND.pressRelease}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid="press-release-download"
                  className="inline-flex items-center gap-2 text-white/85 hover:text-[color:var(--gold)] transition-colors group"
                >
                  <span>Malie — Complete PR Media Package</span>
                  <span className="text-xs uppercase tracking-[0.2em] gold-text opacity-70 group-hover:opacity-100">PDF</span>
                </a>
                <p className="text-xs text-white/45 mt-1">Bio, hi-res photos & quotes</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 grid place-items-center rounded-full glass shrink-0"><MapPin size={14} className="gold-text" /></span>
              <div>
                <p className="overline mb-1">Based In</p>
                <p className="text-white/85">Norfolk, VA — touring worldwide</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="booking-form"
          className="lg:col-span-7 glass-strong rounded-2xl p-6 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required testid="booking-full-name" />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="booking-email" />
            <Field label="Event Type" name="event_type" value={form.event_type} onChange={onChange} required testid="booking-event-type" placeholder="Festival, club, brand, etc." />
            <Field label="Location" name="location" value={form.location} onChange={onChange} required testid="booking-location" />
            <Field label="Budget" name="budget" value={form.budget} onChange={onChange} testid="booking-budget" placeholder="$$$" />
            <Field label="Date" name="event_date" type="date" value={form.event_date} onChange={onChange} testid="booking-date" />
          </div>

          <div>
            <label className="overline block mb-2">Additional Details</label>
            <textarea
              name="details"
              value={form.details}
              onChange={onChange}
              rows={5}
              placeholder="Tell us about the vision."
              data-testid="booking-details"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 outline-none focus:border-[color:var(--gold)] focus:bg-white/8 transition-all resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit"
            className="btn-gold w-full sm:w-auto disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Submit Request"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testid, placeholder }) => (
  <div>
    <label className="overline block mb-2">{label}{required ? " *" : ""}</label>
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-4 outline-none focus:border-[color:var(--gold)] focus:bg-white/8 transition-all"
    />
  </div>
);
